import React, { useRef, useState, useEffect } from 'react';
import { Modifier, EditorState } from 'draft-js';
export const HIGHLIGHT_PREFIX = 'HIGHLIGHT_';

export const COLORS = [
  { label: 'Red', value: '#ff0000' },
  { label: 'Yellow', value: '#ffff00' },
  { label: 'Green', value: '#00ff00' },
  { label: 'Blue', value: '#00ffff' },
  { label: 'Purple', value: '#ff00ff' },
];

// THIS IS REQUIRED FOR HIGHLIGHT TO SHOW IN EDITOR
export const HIGHLIGHT_STYLES = {
  HIGHLIGHT_FF0000: { backgroundColor: '#ff0000' },
  HIGHLIGHT_FFFF00: { backgroundColor: '#ffff00' },
  HIGHLIGHT_00FF00: { backgroundColor: '#00ff00' },
  HIGHLIGHT_00FFFF: { backgroundColor: '#00ffff' },
  HIGHLIGHT_FF00FF: { backgroundColor: '#ff00ff' },
};

const HighlightedText = ({ editorState, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef(null);
  const containerRef = useRef(null);

  const handleButtonClick = (e) => {
    e.preventDefault();
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      pickerRef.current = {
        selection,
        content: editorState.getCurrentContent(),
        inlineStyles: editorState.getCurrentInlineStyle(),
      };
    }
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const getSafeSelection = () => pickerRef.current ? pickerRef.current.selection : editorState.getSelection();
  const getSafeContent = () => pickerRef.current ? pickerRef.current.content : editorState.getCurrentContent();
  const getSafeInlineStyle = () => pickerRef.current ? pickerRef.current.inlineStyles : editorState.getCurrentInlineStyle();

  const applyColor = (hex) => {
    const selection = getSafeSelection();
    if (selection.isCollapsed()) return;

    let contentState = getSafeContent();
    const currentStyle = getSafeInlineStyle();

    currentStyle.forEach(style => {
      if (style && style.startsWith(HIGHLIGHT_PREFIX)) {
        contentState = Modifier.removeInlineStyle(contentState, selection, style);
      }
    });

    const styleKey = HIGHLIGHT_PREFIX + hex.replace('#', '').toUpperCase();
    contentState = Modifier.applyInlineStyle(contentState, selection, styleKey);

    const newEditorState = EditorState.push(editorState, contentState, 'change-inline-style');
    const finalState = EditorState.forceSelection(newEditorState, selection);

    onChange(finalState);
    setIsOpen(false);
    pickerRef.current = null;
  };

  const removeHighlight = () => {
    const selection = getSafeSelection();
    if (selection.isCollapsed()) return;

    let contentState = getSafeContent();
    getSafeInlineStyle().forEach(style => {
      if (style && style.startsWith(HIGHLIGHT_PREFIX)) {
        contentState = Modifier.removeInlineStyle(contentState, selection, style);
      }
    });

    const newState = EditorState.push(editorState, contentState, 'change-inline-style');
    onChange(EditorState.forceSelection(newState, selection));

    setIsOpen(false);
    pickerRef.current = null;
  };

  const activeColor = COLORS.find(c =>
    editorState.getCurrentInlineStyle().has(HIGHLIGHT_PREFIX + c.value.replace('#', '').toUpperCase())
  );

  return (
    <div className="rdw-inline-wrapper" ref={containerRef}>
      <div className="highlight-color-picker">
        <button
          type="button"
          className={`highlight-color-button ${activeColor ? 'active' : ''} ${isOpen ? 'open' : ''}`}
          onMouseDown={e => e.preventDefault()}
          onClick={handleButtonClick}
          style={{
            backgroundColor: activeColor ? activeColor.value : '#fff',
          }}
          aria-label="Text Highlight"
        >
          Text Highlight Color
        </button>

        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            background: '#fff',
            border: '1px899 solid #ddd',
            borderRadius: '6px',
            padding: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '6px',
            marginTop: '4px'
          }}>
            {COLORS.map(color => (
              <button
                key={color.value}
                type="button"
                style={{
                  width: '28px',
                  height: '28px',
                  backgroundColor: color.value,
                  border: '2px solid #fff',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title={color.label}
                onClick={() => applyColor(color.value)}
              />
            ))}
            <button
              type="button"
              onClick={removeHighlight}
              style={{
                gridColumn: '1 / -1',
                background: '#f0f0f0',
                padding: '4px',
                borderRadius: '4px',
                fontSize: '12px'
              }}
            >
              ✕ Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HighlightedText;